import * as React from 'react';
import {
    Box,
    Button,
    Chip,
    Container,
    Grid2,
    Link as MuiLink,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Toolbar,
    Typography
} from '@mui/material';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import ModernNavbar from '../setup/ModernNavbar';
import Footer from '../setup/Footer';
import Loading from '../../BuiltInComponent/Loading';
import DateTimePicker from '../../BuiltInComponent/DateTimePicker';
import AutoComplete from '../../BuiltInComponent/AutoComplete';
import { getUnits } from '../../features/AdminManagement/unitsSlice';
import { getAttendanceSites } from '../../features/AdminManagement/attendanceSiteSlice';
import { getMobileAttendanceReport } from '../../features/PayrollManagement/mobileAttendanceReportSlice';

const formatDate = (value) => dayjs(value).format('DD-MMM-YYYY');
const formatTime = (value) => dayjs(value).format('hh:mm:ss A');

export default function MobileAttendanceReport() {
    const dispatch = useDispatch();
    const { rows, isLoading, isError, message } = useSelector((state) => state.mobileAttendanceReport);
    const { units } = useSelector((state) => state.units);
    const { sites } = useSelector((state) => state.attendanceSites);
    const [filters, setFilters] = React.useState({
        targetDate: dayjs(),
        fromDate: null,
        toDate: null,
        unitID: '',
        siteID: ''
    });

    React.useEffect(() => {
        dispatch(getUnits());
        dispatch(getAttendanceSites());
    }, [dispatch]);

    React.useEffect(() => {
        handleFetch();
    }, [dispatch]);

    const handleFetch = () => {
        dispatch(getMobileAttendanceReport({
            targetDate: filters.targetDate ? dayjs(filters.targetDate).format('YYYY-MM-DD') : undefined,
            fromDate: filters.fromDate ? dayjs(filters.fromDate).format('YYYY-MM-DD') : undefined,
            toDate: filters.toDate ? dayjs(filters.toDate).format('YYYY-MM-DD') : undefined,
            unitID: filters.unitID || undefined,
            siteID: filters.siteID || undefined
        }));
    };

    const handleUnitChange = (data) => {
        if (!data) {
            setFilters((prev) => ({ ...prev, unitID: '' }));
            return;
        }

        const keys = Object.keys(data);
        setFilters((prev) => ({ ...prev, unitID: data[keys[0]] }));
    };

    const handleSiteChange = (data) => {
        if (!data) {
            setFilters((prev) => ({ ...prev, siteID: '' }));
            return;
        }

        const keys = Object.keys(data);
        setFilters((prev) => ({ ...prev, siteID: data[keys[0]] }));
    };

    const totalEmployees = new Set((rows || []).map((row) => row.empID)).size;

    return (
        <>
            <ModernNavbar />
            <Toolbar />
            <Container sx={{ pb: 4 }}>
                <Typography sx={{ textAlign: 'center', fontSize: 30, fontWeight: 'bolder', mb: 3 }}>Mobile Attendance Report</Typography>

                <Paper sx={{ p: 3, mb: 3 }}>
                    <Grid2 container spacing={2}>
                        <Grid2 size={3}>
                            <DateTimePicker label='Target Date' value={filters.targetDate} onChange={(value) => setFilters((prev) => ({ ...prev, targetDate: value }))} />
                        </Grid2>
                        <Grid2 size={3}>
                            <DateTimePicker label='From Date' value={filters.fromDate} onChange={(value) => setFilters((prev) => ({ ...prev, fromDate: value }))} />
                        </Grid2>
                        <Grid2 size={3}>
                            <DateTimePicker label='To Date' value={filters.toDate} onChange={(value) => setFilters((prev) => ({ ...prev, toDate: value }))} />
                        </Grid2>
                        <Grid2 size={3}>
                            <AutoComplete
                                listData={units || []}
                                value={(units || []).find((row) => row.unitID === filters.unitID) || null}
                                onChange={handleUnitChange}
                                label='Unit'
                            />
                        </Grid2>
                        <Grid2 size={6}>
                            <AutoComplete
                                listData={sites || []}
                                value={(sites || []).find((row) => row.siteID === filters.siteID) || null}
                                onChange={handleSiteChange}
                                label='Attendance Site'
                            />
                        </Grid2>
                        <Grid2 size={6}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                <Button variant='outlined' onClick={() => setFilters({ targetDate: dayjs(), fromDate: null, toDate: null, unitID: '', siteID: '' })}>Reset</Button>
                                <Button variant='contained' onClick={handleFetch}>Apply</Button>
                            </Box>
                        </Grid2>
                    </Grid2>
                </Paper>

                <Paper sx={{ p: 3, mb: 3 }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Chip color='primary' label={`Total Mobile Punches: ${(rows || []).length}`} />
                        <Chip color='success' label={`Employees: ${totalEmployees}`} />
                    </Box>
                </Paper>

                {isLoading ? <Loading /> : null}
                {isError ? <Typography color='error' sx={{ mb: 2 }}>{message}</Typography> : null}

                {!isLoading ? (
                    <Paper sx={{ p: 2 }}>
                        <TableContainer>
                            <Table size='small'>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Time</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Employee</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Department</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Unit</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Site</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>In/Out</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Verify</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Location</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Map</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(rows || []).length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={10} align='center'>No mobile attendance record found.</TableCell>
                                        </TableRow>
                                    ) : (rows || []).map((row) => {
                                        const hasCoordinates = row.location_latitude !== null && row.location_longitude !== null;
                                        const mapUrl = hasCoordinates ? `https://maps.google.com/?q=${row.location_latitude},${row.location_longitude}` : null;

                                        return (
                                            <TableRow key={row.id} hover>
                                                <TableCell>{formatDate(row.punch_time)}</TableCell>
                                                <TableCell>{formatTime(row.punch_time)}</TableCell>
                                                <TableCell>{row.empName} {row.machineID ? `(${row.machineID})` : ''}</TableCell>
                                                <TableCell>{row.deptName || '-'}</TableCell>
                                                <TableCell>{row.unitDescription || '-'}</TableCell>
                                                <TableCell>{row.matched_site_name || '-'}</TableCell>
                                                <TableCell>{row.in_out_mode || '-'}</TableCell>
                                                <TableCell>{row.verify_mode || '-'}</TableCell>
                                                <TableCell sx={{ minWidth: 250 }}>
                                                    <Typography variant='caption' display='block'>{row.location_address || '-'}</Typography>
                                                    <Typography variant='caption' display='block' color='text.secondary'>
                                                        {hasCoordinates ? `${Number(row.location_latitude).toFixed(6)}, ${Number(row.location_longitude).toFixed(6)}` : 'No coordinates'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {mapUrl ? (
                                                        <MuiLink href={mapUrl} target='_blank' rel='noreferrer' underline='hover'>Open Map</MuiLink>
                                                    ) : '-'}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                ) : null}
            </Container>
            <Footer />
        </>
    );
}